import { ArrowLeft, Menu, Search } from 'lucide-react'
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { useI18n } from '@/i18n'
import { neonStrings } from '@/i18n/neon'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { CATEGORIES } from '@/lib/categories'
import { getTechnique, getChain } from '@/lib/content'
import { getTool } from '@/lib/tools'
import {
  backHintForPath,
  fallbackParent,
  getFromState,
  hasSameAppHistory,
} from '@/lib/navHistory'

type Props = {
  onMenu: () => void
  onSearch: () => void
  title?: string
}

type Crumb = { label: string; current?: boolean }

export function TopBar({ onMenu, onSearch, title }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t, lang, langPath } = useI18n()
  const n = neonStrings(lang)
  const { from, fromLabel } = getFromState(location)
  const rel = location.pathname.replace(new RegExp(`^/${lang}`), '') || '/'
  const isHome = rel === '/' || rel === ''
  const segments = rel.split('/').filter(Boolean)

  // Breadcrumb aus Route-Daten (Labels statt Slugs)
  const crumbs: Crumb[] = []
  const first = segments[0]
  const second = segments[1]
  let isTechnique = false
  if (isHome) crumbs.push({ label: n.home, current: true })
  else if (first === 'explore') {
    crumbs.push({ label: n.explore, current: !searchParams.get('category') })
    const cat = CATEGORIES.find((c) => c.id === searchParams.get('category'))
    if (cat) crumbs.push({ label: cat.label, current: true })
  } else if (first === 'status') crumbs.push({ label: n.status, current: true })
  else if (first === 'attack-chains') {
    crumbs.push({ label: n.chains, current: !second })
    if (second) crumbs.push({ label: getChain(second, lang)?.title || second, current: true })
  } else if (first === 'tools') {
    crumbs.push({ label: n.tools, current: !second })
    if (second) crumbs.push({ label: getTool(second, lang)?.name || second, current: true })
  } else if (first && second) {
    isTechnique = true
    const tech = getTechnique(second, lang)
    const cat = CATEGORIES.find((c) => c.id === first)
    crumbs.push({ label: cat?.label || tech?.category || first })
    if (tech?.subcategory) crumbs.push({ label: tech.subcategory })
    crumbs.push({ label: tech?.title || second, current: true })
  }

  const reviewMode = searchParams.get('review') === '1' || searchParams.get('editor') === '1'
  function toggleReview() {
    const next = new URLSearchParams(searchParams)
    if (reviewMode) { next.delete('review'); next.delete('editor') } else next.set('review', '1')
    setSearchParams(next, { replace: true })
  }

  const hint = from
    ? (fromLabel ? (lang === 'en' ? `to ${fromLabel}` : `zu ${fromLabel}`) : backHintForPath(from, lang))
    : ''
  const backLabel = hint ? (lang === 'en' ? `Back ${hint}` : `Zurück ${hint}`) : t.actions.back

  function goBack() {
    if (from) { navigate(from); return }
    if (hasSameAppHistory()) { navigate(-1); return }
    navigate(fallbackParent(location.pathname, lang))
  }

  void params
  void langPath

  return (
    <header className={`topbar${first === 'tools' ? ' is-tools' : ''}`}>
      {!isHome ? (
        <button type="button" className="btn btn-ghost topbar-back no-print" onClick={goBack} aria-label={backLabel} title={backLabel}>
          <ArrowLeft size={18} />
          <span className="topbar-back-label">{backLabel}</span>
        </button>
      ) : (
        <div className="topbar-back-spacer" aria-hidden="true" />
      )}

      <button type="button" className="btn btn-ghost topbar-menu no-print" onClick={onMenu} aria-label={t.actions.menu}>
        <Menu size={18} />
      </button>

      <div className="topbar-title">
        <strong>{title || t.appTitle}</strong>
        <span className="topbar-path">
          {crumbs.map((c, i) => (
            <span key={i} className={c.current ? 'is-current' : undefined}> / {c.label}</span>
          ))}
        </span>
      </div>

      {isTechnique ? (
        <button type="button" className="btn btn-ghost review-toggle no-print" onClick={toggleReview}>
          {reviewMode ? t.technique.readerMode : t.technique.reviewMode}
        </button>
      ) : null}
      <LanguageSwitcher />
      <button type="button" className="btn no-print" onClick={onSearch}>
        <Search size={16} /> <span className="kbd">Ctrl+K</span>
      </button>
    </header>
  )
}
